import { createContext, useContext, useEffect, useRef, useState } from "react";
import { ArrowRight, CalendarCheck } from "lucide-react";

export const LandingCtx = createContext({ openLead: () => {}, cfg: {} });
export const useLanding = () => useContext(LandingCtx);
export const PLAN_NOTE = "Plano inicial para empresas com até 5 funcionários. Escopo e condições comerciais são apresentados na proposta, antes da contratação.";
export const DEMO = { source: "demonstracao", interest: "demonstracao" };

export const Container = ({ className = "", children }) => <div className={`max-w-7xl mx-auto px-5 sm:px-8 ${className}`}>{children}</div>;
export const Eyebrow = ({ children, light }) => <div className={`font-mono text-[11px] uppercase tracking-[0.18em] mb-4 ${light ? "text-sky-300" : "text-[#0B6FC2]"}`}>{children}</div>;
export const H2 = ({ children, light, className = "" }) => <h2 className={`font-display text-3xl sm:text-4xl lg:text-[2.9rem] font-bold tracking-tight leading-[1.08] ${light ? "text-white" : "text-[#0F172A]"} ${className}`}>{children}</h2>;

export function Reveal({ children, className = "", delay = 0, as: Tag = "div", ...rest }) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setOn(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setOn(true); io.disconnect(); } }, { rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} style={{ transitionDelay: `${delay}ms` }} className={`lp-reveal ${on ? "is-in" : ""} ${className}`} {...rest}>{children}</Tag>;
}

export function PrimaryCta({ label = "QUERO CONHECER A DPDIGI", source, testid, className = "", light }) {
  const { openLead } = useLanding();
  return (
    <button type="button" onClick={() => openLead(source)} data-testid={testid}
      className={`group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-7 py-4 text-[15px] font-bold tracking-wide transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 active:translate-y-0 ${light ? "bg-white text-[#063C6D] hover:bg-sky-50 shadow-lg shadow-black/20" : "bg-[#0B6FC2] text-white hover:bg-[#063C6D] shadow-lg shadow-[#0B6FC2]/25"} ${className}`}>
      {label}<ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
    </button>);
}

export function DemoCta({ testid, className = "", light, label = "AGENDAR UMA DEMONSTRAÇÃO" }) {
  const { openLead } = useLanding();
  return (
    <button type="button" onClick={() => openLead(DEMO.source, { interest: DEMO.interest })} data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 px-7 py-[14px] text-[15px] font-bold tracking-wide transition-[background-color,color,border-color] duration-200 ${light ? "border-white/40 text-white hover:bg-white/10" : "border-[#063C6D]/20 bg-white text-[#063C6D] hover:border-[#0B6FC2] hover:text-[#0B6FC2]"} ${className}`}>
      <CalendarCheck className="h-4 w-4" />{label}
    </button>);
}

export function Price({ light, size = "lg", testid }) {
  const big = size === "xl" ? "text-6xl sm:text-7xl lg:text-8xl" : "text-5xl sm:text-6xl";
  return (
    <div data-testid={testid}>
      <div className={`font-mono text-[11px] uppercase tracking-[0.2em] ${light ? "text-sky-200" : "text-slate-500"}`}>Planos a partir de*</div>
      <div className="flex items-end gap-2 flex-wrap">
        <span className={`font-display font-extrabold tracking-tight leading-none ${big} ${light ? "text-white" : "text-[#0B6FC2]"}`}>R$&nbsp;197</span>
        <span className={`pb-1.5 text-lg sm:text-xl font-semibold leading-tight ${light ? "text-sky-100" : "text-[#063C6D]"}`}>/mês</span>
      </div>
    </div>);
}
